import React, { useMemo, useState } from "react";
import {
  Col,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import classnames from "classnames";
import SimpleBar from "simplebar-react";
import { useQuery, useQueryClient } from "react-query";

import bell from "../../assets/images/bell.svg";
import { getNotifications, updateNotification } from "../../modules/Notifications/services/notificationApi";

const NotificationDropdown = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [isNotificationDropdown, setIsNotificationDropdown] = useState(false);
    const [activeTab, setActiveTab] = useState("notifications");
    const [readNotifications, setReadNotifications] = useState([]);

    const toggleNotificationDropdown = async () => {
      if (isNotificationDropdown) {
        await queryClient.invalidateQueries("notifications");
      } else {
        setReadNotifications([]);
      }

      setIsNotificationDropdown(!isNotificationDropdown);
    };

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
        setActiveTab(tab);
        }
    };

    const {
      isLoading,
      isError,
      data: notifications = [],
    } = useQuery("notifications", getNotifications);

    const isRead = (notification) => notification.status?.toLowerCase() === "lue";

    const visibleNotifications = useMemo(
      () =>
        notifications.filter(
          (n) => !isRead(n)
        ),
      [notifications]
    );

    const unreadCount = useMemo(
    () =>
      notifications.filter(
        (n) =>
          !isRead(n) &&
          !readNotifications.includes(n.id)
      ).length,
    [notifications, readNotifications]
  );

  const inspectionNotifications = useMemo(
    () =>
      visibleNotifications.filter((n) => {
        const type = n.type?.toLowerCase() || "";

        return (
          type.includes("inspection") ||
          type.includes("exterior") ||
          type.includes("interior")
        );
      }),
    [visibleNotifications]
  );

  const incidentNotifications = useMemo(
    () =>
      visibleNotifications.filter((n) => {
        const type = n.type?.toLowerCase() || "";
        return type.includes("incident");
      }),
    [visibleNotifications]
  );
  const demandesNotifications = useMemo(
    () =>
      visibleNotifications.filter((n) => {
        const type = n.type?.toLowerCase() || "";
        return type.includes("demande");
      }),
    [visibleNotifications]
  );

  const handleNotificationClick = async (notification) => {
      try {
        if (
          !isRead(notification) &&
          !readNotifications.includes(notification.id)
        ) {
          await updateNotification(notification.id, "Lue");

          setReadNotifications((prev) => [
            ...prev,
            notification.id,
          ]);
        }
      } catch (error) {
        console.error(error);
      }
    };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getNotificationIcon = (type) => {
    const lowerType = type?.toLowerCase() || "";

    if (
      lowerType.includes("inspection") ||
      lowerType.includes("exterior") ||
      lowerType.includes("interior")
    ) {
      return {
        icon: "bx bx-search-alt",
        className: "bg-success-subtle text-success",
      };
    }

    if (lowerType.includes("incident")) {
      return {
        icon: "bx bx-error-circle",
        className: "bg-danger-subtle text-danger",
      };
    }

    if (lowerType.includes("affectation")) {
      return {
        icon: "bx bx-car",
        className: "bg-info-subtle text-info",
      };
    }

    return {
      icon: "bx bx-bell",
      className: "bg-primary-subtle text-primary",
    };
  };

  const renderNotifications = (items) => {
    if (isLoading) {
      return (
        <div className="text-center py-4">
          <p className="mb-0">Chargement...</p>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="text-center py-4">
          <p className="text-danger mb-0">
            Erreur lors du chargement des notifications
          </p>
        </div>
      );
    }

    if (!items.length) {
      return (
        <div className="text-center py-4">
          <div className="w-25 w-sm-50 pt-3 mx-auto">
            <img
              src={bell}
              className="img-fluid"
              alt="demandes"
            />
          </div>
          <p className="text-muted mt-4 mb-0">
            Aucune notification de type {activeTab} pour l'instant
          </p>
        </div>
      );
    }

    return items.map((notification) => {
      const { icon, className } = getNotificationIcon(notification.type);

      return (
        <div
            key={notification.id}
            onClick={() => handleNotificationClick(notification)}
            style={{ cursor: "pointer" }}
            className={`text-reset notification-item d-block dropdown-item position-relative ${
              readNotifications.includes(notification.id) ||
              isRead(notification)
                ? ""
                : "active"
            }`}
        >
          <div className="d-flex">
            <div className="avatar-xs me-3">
              <span
                className={`avatar-title rounded-circle fs-16 ${className}`}
              >
                <i className={icon}></i>
              </span>
            </div>

            <div className="flex-grow-1">
              <Link to="#" className="stretched-link">
                <h6 className="mt-0 mb-1 fs-13 fw-semibold">
                  {notification.title}
                </h6>
              </Link>

              <div className="fs-13 text-muted">
                <p className="mb-1">{notification.message}</p>
              </div>

              <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                <span>
                  <i className="mdi mdi-clock-outline"></i>{" "}
                  {formatDate(notification.createdAt)}
                </span>
              </p>
            </div>

            {!isRead(notification) && !readNotifications.includes(notification.id) && (
              <div className="px-2">
                <span className="badge bg-danger">
                  Nouveau
                </span>
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <Dropdown
      isOpen={isNotificationDropdown}
      toggle={toggleNotificationDropdown}
      className="topbar-head-dropdown ms-1 header-item"
    >
      <DropdownToggle
        type="button"
        tag="button"
        className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
      >
        <i className="bx bx-bell fs-22"></i>

        {unreadCount > 0 && (
          <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
            {unreadCount}
          </span>
        )}
      </DropdownToggle>

      <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0" style={{ minWidth: "500px" }}>
        <div className="dropdown-head bg-primary bg-pattern rounded-top">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0 fs-16 fw-semibold text-white">
                  Notifications
                </h6>
              </Col>

              <div className="col-auto dropdown-tabs">
                <span className="badge bg-light-subtle text-body fs-13">
                  {unreadCount} Nouvelle {unreadCount > 1 ? "s" : ""}
                </span>
              </div>
            </Row>
          </div>

          <div className="px-2 pt-2">
            <Nav className="nav-tabs dropdown-tabs nav-tabs-custom">
              <NavItem>
                <NavLink
                  href="#"
                  className={classnames({
                    active: activeTab === "notifications",
                  })}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("notifications");
                  }}
                >
                  Tous ({visibleNotifications.length})
                </NavLink>
              </NavItem>

              {/* <NavItem>
                <NavLink
                  href="#"
                  className={classnames({
                    active: activeTab === "2",
                  })}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("2");
                  }}
                >
                  Inspections ({inspectionNotifications.length})
                </NavLink>
              </NavItem> */}

              <NavItem>
                <NavLink
                  href="#"
                  className={classnames({
                    active: activeTab === "incidents",
                  })}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("incidents");
                  }}
                >
                  Incidents ({incidentNotifications.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className={classnames({
                    active: activeTab === "demandes",
                  })}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("demandes");
                  }}
                >
                  Demandes ({demandesNotifications.length})
                </NavLink>
              </NavItem>
            </Nav>
          </div>
        </div>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="notifications" className="py-2 ps-2">
            <SimpleBar style={{ maxHeight: "300px" }}
              className="pe-2"
            >
              {renderNotifications(visibleNotifications)}

              {!isLoading && notifications.length > 0 && (
                <div className="my-3 text-center">
                  <Link
                    to="/notifications"
                    className="btn btn-soft-success waves-effect waves-light"
                  >
                    Voir toutes les Notifications
                    <i className="ri-arrow-right-line align-middle ms-1"></i>
                  </Link>
                </div>
              )}
            </SimpleBar>
          </TabPane>

          <TabPane tabId="incidents" className="py-2 ps-2">
            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
              {renderNotifications(incidentNotifications)}
            </SimpleBar>
          </TabPane>

          <TabPane tabId="demandes" className="py-2 ps-2">
            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
              {renderNotifications(demandesNotifications)}
            </SimpleBar>
          </TabPane>
        </TabContent>
      </DropdownMenu>
    </Dropdown>
  );
};

export default NotificationDropdown;